/**
 * Top-up country/currency shortlist for THIS client (PRIVATE_BANK_ONBOARDING_V2.md §5, REQ-020..022).
 * Customer-JWT zone — GET /api/v1/showcase/me/topup-options. The backend returns the market shortlist
 * (UAE, and Russia only when the client carries a RU-signal: edge geo-IP ∨ ru-locale), NOT the full ISO
 * dictionary. RUB is present only on that same signal. Per-request (the signal can change with locale), so
 * NOT module-cached like the static reference lists.
 */
import { http } from './http';

/**
 * Deposit-rail form config (EPHEMERAL_DEPOSIT_WALLETS_DESIGN §6.1) — BACKEND-driven, never a
 * hardcoded FE list: on these networks the client gets an ephemeral deposit address after submit
 * (no client-declared address field), and the O7 minimum is shown before they type an amount.
 */
export interface TopUpRailOptions {
  /**
   * The rail is on (docs/BO_CARD_PROVIDER_WALLET.md §6.2). Tells "rail off" from "rail on, nothing for this client":
   * without it an empty list reads as "not loaded" and every network opens. Absent from an older core.
   */
  enabled?: boolean;
  networks: string[];           // e.g. ['TRON'] — reference codes (networks.json); the union of `pairs`
  assets: string[];             // e.g. ['USDT']; the union of `pairs`
  /** Coin × network pairs the provider of this client's card can take (§6.2). Absent from an older core. */
  pairs?: RailPair[];
  minDepositUsd: string | null; // anti-griefing floor; null = not configured
  /**
   * How many days an arrival under `minDepositUsd` may wait at most before it is screened and credited (the address
   * lifetime the floor deferral waits out). Absent from an older core.
   */
  belowMinHoldDays?: number | null;
}

export interface RailPair {
  asset: string;    // 'USDT'
  network: string;  // 'TRON'
}

/** Pairs are known only when the rail is on and the core sent them; otherwise nothing is narrowed by pairs. */
function knownPairs(rail: TopUpRailOptions | null | undefined): RailPair[] | null {
  return rail?.enabled === true && Array.isArray(rail.pairs) ? rail.pairs : null;
}

/**
 * The rail networks this coin can be sent on for this client, upper-case — or null when pairs are not known (options
 * not loaded, rail off, an older core), in which case the caller keeps its network-only behaviour. An empty list
 * means "none": the coin has no pair and no rail network may be picked for it.
 */
export function railNetworksForAsset(rail: TopUpRailOptions | null | undefined, asset: string): string[] | null {
  const pairs = knownPairs(rail);
  if (!pairs) return null;
  const coin = asset.trim().toUpperCase();
  return [...new Set(pairs.filter(p => p.asset.toUpperCase() === coin).map(p => p.network.toUpperCase()))];
}

/** Coins with at least one pair for this client, in the core's order — null when pairs are not known. */
export function railAssetsWithPairs(rail: TopUpRailOptions | null | undefined): string[] | null {
  const pairs = knownPairs(rail);
  if (!pairs) return null;
  return [...new Set(pairs.map(p => p.asset.toUpperCase()))];
}

/** Display names of the rail networks (reference code → what the client reads). Unknown codes show as-is. */
const RAIL_NETWORK_LABELS: Record<string, string> = {
  TRON: 'TRC-20', ETHEREUM: 'ERC-20', TON: 'TON', SOLANA: 'Solana',
};

export function railNetworkLabel(code: string): string {
  return RAIL_NETWORK_LABELS[code.trim().toUpperCase()] ?? code;
}

/**
 * A network name must not wrap at its hyphen ("TRC-" / "20" on two lines — seen in zh, where the line breaks
 * anywhere): the hyphen becomes a non-breaking one (U+2011), which reads the same.
 */
function unbreakable(label: string): string {
  return label.replace(/-/g, '\u2011');
}

type Translate = (key: string, params: Record<string, string>) => string;

function joinList(items: string[], locale: string): string {
  try {
    // «TRC-20 или ERC-20» — the prototype's wording (D): the client picks one network.
    return new Intl.ListFormat(locale, { style: 'long', type: 'disjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/**
 * The crypto method's subtitle from what THIS client is actually offered (docs/BO_CARD_PROVIDER_WALLET.md §6.2):
 * one "coin on networks" group per coin, in the core's order, e.g. "USDT on TRC-20 and Solana · USDC on TRC-20".
 * Null when pairs are unknown or empty — the caller keeps the generic text rather than naming nothing.
 */
export function cryptoOfferSubtitle(
  rail: TopUpRailOptions | null | undefined,
  t: Translate,
  locale: string,
): string | null {
  const coins = railAssetsWithPairs(rail);
  if (!coins || coins.length === 0) return null;
  return coins
    .map(coin => {
      const nets = (railNetworksForAsset(rail, coin) ?? []).map(railNetworkLabel).map(unbreakable);
      const key = nets.length === 1 ? 'vitrine.topup.cryptoSubPairOne' : 'vitrine.topup.cryptoSubPairMany';
      return t(key, { coin, networks: joinList(nets, locale) });
    })
    .join(', ');
}

export interface TopUpOptions {
  countries: string[];   // ISO-3166-1 alpha-3, market shortlist
  currencies: string[];  // ISO-4217 fiat shortlist (RUB only on RU-signal)
  ruSignal: boolean;     // whether Russia/RUB are offered — lets the UI explain the shortlist if needed
  rail: TopUpRailOptions;
  /**
   * P% the client pays, as a decimal string ("5.00"), or null when the backend cannot resolve it yet.
   * The simplified crypto screen states the fee BEFORE any amount exists (REQ-S6), so it cannot come from
   * the estimate — that needs an amount. Null ⇒ the screen omits the line rather than guessing a number
   * next to an address the client is about to send money to.
   */
  clientPercent: string | null;
  /**
   * PayFlow spec 07 В1: present while Transfer Hub funds bank/cash top-ups — the bank/cash form then asks how much
   * to credit to the card, in `cardCurrency`, and the currency picker becomes the currency the client sends in (the
   * amount in it is the provider's quote). Absent/null: the form as it always was.
   */
  fiatCredit?: { cardCurrency: string } | null;
}

/**
 * The card currency the bank/cash amount is named in (В1), or null when the form asks for the amount to send as it
 * always did — crypto, an older core, or Transfer Hub funding off.
 */
export function creditCurrencyFor(
  kind: 'CRYPTO' | 'BANK' | 'CASH',
  opts: Pick<TopUpOptions, 'fiatCredit'> | null | undefined,
): string | null {
  if (kind === 'CRYPTO') return null;
  const ccy = opts?.fiatCredit?.cardCurrency;
  return ccy ? ccy.toUpperCase() : null;
}

export async function loadTopUpOptions(): Promise<TopUpOptions> {
  return (await http.get<TopUpOptions>('/v1/showcase/me/topup-options')).data;
}
